import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to validate logo URLs
const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || 
         url.includes('amazonaws.com') || 
         url.includes('crystalmedia') ||
         url.startsWith('/');
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, serviceRes] = await Promise.all([
          fetch(`${API_URL}/api/content/settings`),
          fetch(`${API_URL}/api/services/${slug}`)
        ]);

        const settingsData = await settingsRes.json();
        
        if (serviceRes.ok) {
          const serviceData = await serviceRes.json();
          setService(serviceData);
        } else {
          navigate('/services');
          return;
        }

        setSettings(settingsData);
        const photoLogo = settingsData.photo_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(photoLogo) ? photoLogo : '');
      } catch (error) {
        console.error('Error fetching data:', error);
        navigate('/services');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  // Render content blocks
  const renderBlock = (block, index) => {
    const bgClass = block.background === 'light' ? 'bg-[#f5f5f5]' : block.background === 'accent' ? 'bg-black text-white' : 'bg-white';
    
    switch (block.type) {
      case 'text':
        return (
          <section key={block.id} className={`py-16 lg:py-24 ${bgClass}`}>
            <div className={`max-w-4xl mx-auto px-6 lg:px-12 ${block.layout === 'centered' ? 'text-center' : ''}`}>
              {block.subheading && (
                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-4">{block.subheading}</p>
              )}
              {block.heading && (
                <h2 className="font-presti text-2xl md:text-3xl lg:text-4xl mb-6">{block.heading}</h2>
              )}
              {block.content && (
                <p className="text-lg leading-relaxed opacity-60 whitespace-pre-line">{block.content}</p>
              )}
            </div>
          </section>
        );

      case 'image': {
        const svcImgFp = window.innerWidth < 768
          ? (block.image_focal_mobile || { x: 50, y: 50 })
          : (block.image_focal_desktop || { x: 50, y: 50 });
        return (
          <section key={block.id} className={`py-16 lg:py-24 ${bgClass}`}>
            <div className={`max-w-6xl mx-auto px-6 lg:px-12 ${block.layout === 'centered' ? 'text-center' : ''}`}>
              {block.image_url && (
                <div className="relative overflow-hidden rounded-lg shadow-lg">
                  <img 
                    src={block.image_url} 
                    alt={block.image_caption || service?.title} 
                    className="w-full h-auto"
                    style={{ objectPosition: `${svcImgFp.x}% ${svcImgFp.y}%` }}
                  />
                </div>
              )}
              {block.image_caption && (
                <p className="text-sm text-black/40 mt-4 text-center">{block.image_caption}</p>
              )}
            </div>
          </section>
        );
      }

      case 'gallery':
        return (
          <section key={block.id} className={`py-16 lg:py-24 ${bgClass}`}>
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              {block.heading && (
                <h2 className="font-presti text-2xl md:text-3xl text-center mb-12">{block.heading}</h2>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {block.gallery_images?.map((img, i) => (
                  <div key={i} className="aspect-square overflow-hidden rounded-lg">
                    <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'video':
        return (
          <section key={block.id} className={`py-16 lg:py-24 ${bgClass}`}>
            <div className="max-w-5xl mx-auto px-6 lg:px-12">
              {block.video_url && (
                <div className="relative aspect-video overflow-hidden rounded-lg shadow-lg">
                  <video 
                    src={block.video_url} 
                    controls 
                    className="w-full h-full"
                    poster={block.image_url}
                  />
                </div>
              )}
            </div>
          </section>
        );

      case 'quote':
        return (
          <section key={block.id} className={`py-20 lg:py-32 ${bgClass}`}>
            <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
              <blockquote className="font-presti text-2xl md:text-3xl lg:text-4xl italic leading-relaxed mb-6">
                "{block.quote_text}"
              </blockquote>
              {block.quote_author && (
                <cite className="text-sm tracking-[0.15em] uppercase opacity-50 not-italic">
                  — {block.quote_author}
                </cite>
              )}
            </div>
          </section>
        );

      case 'stats':
        return (
          <section key={block.id} className={`py-20 lg:py-28 ${bgClass}`}>
            <div className="max-w-5xl mx-auto px-6">
              <div className={`grid grid-cols-2 md:grid-cols-${block.stats?.length || 3} gap-8 lg:gap-16`}>
                {block.stats?.map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="font-presti text-4xl lg:text-5xl mb-2">{stat.number}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase opacity-40">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'faq':
        return (
          <section key={block.id} className={`py-16 lg:py-24 ${bgClass}`}>
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
              {block.heading && (
                <h2 className="font-presti text-2xl md:text-3xl text-center mb-12">{block.heading}</h2>
              )}
              <div className="space-y-4">
                {block.faqs?.map((faq, i) => (
                  <div key={i} className="border border-black/10 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === `${block.id}-${i}` ? null : `${block.id}-${i}`)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-black/[0.02] transition-colors"
                    >
                      <span className="font-medium pr-4">{faq.question}</span>
                      <ChevronDown 
                        size={20} 
                        className={`opacity-40 flex-shrink-0 transition-transform duration-300 ${
                          expandedFaq === `${block.id}-${i}` ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${
                      expandedFaq === `${block.id}-${i}` ? 'max-h-96' : 'max-h-0'
                    }`}>
                      <p className="px-6 pb-5 opacity-50 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

      case 'cta':
        return (
          <section key={block.id} className={`py-20 lg:py-28 ${bgClass}`}>
            <div className="max-w-2xl mx-auto px-6 text-center">
              {block.heading && (
                <h2 className="font-presti text-2xl md:text-3xl mb-6">{block.heading}</h2>
              )}
              {block.content && (
                <p className="opacity-50 mb-8">{block.content}</p>
              )}
              <Link
                to={block.cta_link || '/inquire'}
                className={`inline-flex items-center gap-3 px-10 py-4 text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                  block.background === 'accent' 
                    ? 'bg-white text-black hover:bg-white/90' 
                    : 'bg-black text-white hover:bg-black/80'
                }`}
              >
                <span>{block.cta_text || 'Get Started'}</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <p className="text-black/30 animate-pulse">Loading...</p>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Shared Header */}
      <Header logoUrl={logoUrl} variant="light" />

      {/* Back Link */}
      <div className="pt-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-black/40 hover:text-black transition-colors"
          >
            <ArrowLeft size={14} />
            <span>All Services</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-8 pb-16 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-presti text-4xl md:text-5xl lg:text-6xl text-black mb-6" data-testid="service-title">
            {service.title}
          </h1>
          {service.short_description && (
            <p className="text-black/50 text-lg max-w-2xl mx-auto leading-relaxed">
              {service.short_description}
            </p>
          )}
        </div>
      </section>

      {/* Cover Image */}
      {service.cover_image_url && (
        <section className="pb-16 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="relative aspect-[21/9] overflow-hidden rounded-lg shadow-lg">
              <img 
                src={service.cover_image_url} 
                alt={service.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>
      )}

      {/* Content Blocks */}
      {service.content_blocks && service.content_blocks.length > 0 ? (
        service.content_blocks
          .sort((a, b) => a.order - b.order)
          .map((block, index) => renderBlock(block, index))
      ) : (
        <section className="py-20 text-center">
          <p className="text-black/30">Content coming soon...</p>
        </section>
      )}

      {/* Default CTA if no blocks */}
      {(!service.content_blocks || service.content_blocks.length === 0) && (
        <section className="py-20 lg:py-28 bg-white border-t border-black/5">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h2 className="font-presti text-2xl md:text-3xl text-black mb-6">
              Interested in {service.title}?
            </h2>
            <p className="text-black/50 mb-8">
              Get in touch to discuss how we can capture your special moments.
            </p>
            <Link
              to="/inquire"
              className="inline-flex items-center gap-3 px-10 py-4 bg-black text-white text-xs tracking-[0.2em] uppercase hover:bg-black/80 transition-colors duration-300"
            >
              <span>Inquire Now</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-black/5 bg-[#fafafa]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {logoUrl ? (
            <img src={logoUrl} alt="Crystal Media" className="h-6 w-auto opacity-50" />
          ) : (
            <span className="font-presti text-xs tracking-[0.2em] text-black/40">CRYSTAL MEDIA</span>
          )}
          <p className="text-[10px] tracking-[0.15em] uppercase text-black/30">
            © 2025 Crystal Media. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServiceDetailPage;
